import type { RuntimeAdapter, SwfEvent, SwfLoadOptions } from "./swfTypes";

// Ruffle's public JS API is not fully typed upstream; declare the minimal
// surface this adapter depends on so the rest of the app never needs to
// know Ruffle's shape.
interface RufflePlayerElement extends HTMLElement {
  load(options: {
    url: string;
    autoplay?: "on" | "off";
    scale?: string;
  }): Promise<void>;
  play(): void;
  pause(): void;
  set volume(v: number);
  addEventListener(
    type: "loadedmetadata" | "error",
    listener: (e: Event) => void,
  ): void;
}

interface RuffleAPI {
  newest(): { createPlayer(): RufflePlayerElement };
}

declare global {
  interface Window {
    RufflePlayer?: RuffleAPI;
  }
}

const RUFFLE_BASE_URL = import.meta.env.VITE_RUFFLE_BASE_URL ?? "/ruffle";

let ruffleScriptPromise: Promise<void> | null = null;

/** Loads the Ruffle bootstrap script exactly once, regardless of how many players exist. */
function loadRuffleScript(): Promise<void> {
  if (window.RufflePlayer) return Promise.resolve();
  if (ruffleScriptPromise) return ruffleScriptPromise;

  ruffleScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${RUFFLE_BASE_URL}/ruffle.js`;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Ruffle runtime script"));
    document.head.appendChild(script);
  });
  return ruffleScriptPromise;
}

export class RuffleAdapter implements RuntimeAdapter {
  private player: RufflePlayerElement | null = null;
  private container: HTMLElement | null = null;
  private eventHandler: ((event: SwfEvent) => void) | null = null;
  private errorHandler: ((error: Error) => void) | null = null;

  async init(): Promise<void> {
    await loadRuffleScript();
    if (!window.RufflePlayer) {
      throw new Error("Ruffle runtime failed to initialize");
    }
  }

  async load(container: HTMLElement, options: SwfLoadOptions): Promise<void> {
    if (!window.RufflePlayer) {
      throw new Error("RuffleAdapter.init() must resolve before load()");
    }
    this.container = container;

    const ruffle = window.RufflePlayer.newest();
    const player = ruffle.createPlayer();
    player.style.width = "100%";
    player.style.height = "100%";
    player.style.display = "block";
    container.innerHTML = "";
    container.appendChild(player);
    this.player = player;

    player.addEventListener("error", () => {
      this.errorHandler?.(new Error("Ruffle failed to play this SWF"));
    });

    // Bridge AS3 ExternalInterface.call('reactBridge', ...) into typed SwfEvents.
    // Ruffle exposes JS-callable globals attached to the player element for
    // ExternalInterface support; this is intentionally the only file that
    // knows that wiring.
    (window as unknown as Record<string, unknown>).__swfBridge = (
      raw: string,
    ) => {
      try {
        const event = JSON.parse(raw) as SwfEvent;
        this.eventHandler?.(event);
      } catch {
        // Malformed payload from content — ignore rather than crash the player.
      }
    };

    await player.load({
      url: options.url,
      autoplay: options.autoplay === false ? "off" : "on",
      scale: options.scaleMode === "cover" ? "exactfit" : "showall",
    });
  }

  play(): void {
    this.player?.play();
  }

  pause(): void {
    this.player?.pause();
  }

  stop(): void {
    this.player?.pause();
  }

  resize(_width: number, _height: number): void {
    // Sized responsively via CSS (100% width/height). Ruffle automatically detects container size changes.
  }

  requestFullscreen(): void {
    this.container?.requestFullscreen?.();
  }

  destroy(): void {
    if (this.container) this.container.innerHTML = "";
    this.player = null;
    this.eventHandler = null;
    this.errorHandler = null;
  }

  onEvent(handler: (event: SwfEvent) => void): void {
    this.eventHandler = handler;
  }

  onError(handler: (error: Error) => void): void {
    this.errorHandler = handler;
  }
}
